import { MODE_CONTACT } from 'client-portal/utils/prescreener/constants';
import { action } from '@ember/object';
import { createElement, error } from 'client-portal/utils/banners';
import { isRelatedClientValid } from 'client-portal/utils/contact/validate-related-client';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';

export default class ContactWidgetController extends Controller {
  @service iframe;
  @service router;
  @service currentPractice;
  @service session;
  @tracked contentLoading = false;
  @tracked complete = false;
  @tracked clinicianId = null;
  @service analytics;
  @service contactSteps;
  @service prescreener;
  @service intl;
  @service recaptcha;

  get modeContact() {
    return MODE_CONTACT;
  }

  get client() {
    return this.model.client;
  }

  get clinician() {
    return this.model.clinician;
  }

  get name() {
    return this.clinician ? this.clinician.get('fullName') : this.currentPractice.get('fullName');
  }

  get showWizardBack() {
    return !this.complete && this.contactSteps.isEnabled && !this.contactSteps.isFirst;
  }

  @action
  close() {
    this.iframe.close();
  }

  @action
  goBack() {
    this.contactSteps.goBack();
    this.iframe.syncAfterStepChange();
  }

  @(task(function* () {
    try {
      this.setRecaptchaResponse();
      this.buildNestedAttributes();
      yield this.attachPrescreenerAnswers(this.client);

      if (!this.validate()) {
        return;
      }

      let featureEmbeddings = this.prescreener.isPrescreenerEnabled ? ['prescreenerAnswer'] : [];

      yield this.client.save({
        adapterOptions: {
          embed: ['emails', 'phones', 'clientRelationships', ...featureEmbeddings],
        },
      });

      this.analytics.trackAnonymously('Contact Form Send Message Clicked', {
        object: 'Contact Form Send Message',
        action: 'Clicked',
        'site_name': this.session.siteName,
        'referral_channel': this.session.referralChannel,
      });

      this.complete = true;
      this.iframe.contactFormSubmitted();
      this.iframe.broadcastHeight();
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
      yield this.showErrorMessage(err);
    }
  }).drop())
  saveTask;

  validate() {
    this.client.validate();
    this.client.set('_isDirty', true);

    const clientValid = this.client.validations.isValid;
    const emailValid = this.client.emails.at(0).validations.isValid;
    const phoneValid = this.client.phones.at(0).validations.isValid;

    let relatedClientValid = true;

    if (['ClientContact', 'ClientCouple'].includes(this.client.get('thisType'))) {
      relatedClientValid = isRelatedClientValid(
        this.client.clientRelationships.at(0)?.relatedClient
      );
    }

    let prescreenerValid = true;
    if (this.prescreener.isPrescreenerEnabled) {
      prescreenerValid = this.prescreener.validation.validateAll();
    }

    return clientValid && emailValid && phoneValid && relatedClientValid && prescreenerValid;
  }

  async attachPrescreenerAnswers() {
    if (this.prescreener.isPrescreenerEnabled) {
      let prescreenerAnswer = await this.prescreener.getContactFormAnswerData();
      this.client.set('prescreenerAnswer', prescreenerAnswer);
      this.prescreener.updateClientsForAnswers(this.client);
    }
  }

  setRecaptchaResponse() {
    if (this.client.skipRecaptchaValidation) return;

    const response = this.recaptcha.getResponse();
    this.client.set('recaptchaResponse', response);
  }

  async showErrorMessage(err) {
    let { errors } = err;
    let content = createElement('ul');
    let title = 'Unable to save';

    if (!errors || !Array.isArray(errors)) {
      content.appendChild(createElement('li', { innerHTML: 'An unexpected error occurred' }));
      error({ title, content });
      return;
    }

    for (const { title, meta, detail } of errors) {
      let innerHTML = title;
      if (title.indexOf('reCAPTCHA verification failed') >= 0) {
        await this.recaptcha.reset();
      } else if (title === 'duplicate-minor-email') {
        innerHTML = this.intl.t(`errors.appointment.duplicateEmail.minorMessage`);
      } else if (title === 'duplicate-couple-email') {
        innerHTML = this.intl.t(`errors.appointment.duplicateEmail.coupleMessage`);
      } else if (
        title.indexOf('Internal Server Error') >= 0 ||
        title.indexOf('Feature not enabled') >= 0
      ) {
        // Do nothing
      } else if (!meta || title.indexOf('Too many requests') >= 0) {
        innerHTML = title;
      } else {
        innerHTML = `${meta.type} ${meta.attribute} ${detail}`;
      }
      content.appendChild(createElement('li', { innerHTML }));
    }

    error({ title, content });
  }

  buildNestedAttributes() {
    try {
      const clientType = this.client.get('thisType');

      if (!['ClientCouple', 'ClientContact'].includes(clientType)) {
        return;
      }

      const relatedClient = this.client.clientRelationships.at(0)?.relatedClient;

      if (!relatedClient) {
        return;
      }

      let usePrescreener = this.prescreener.isPrescreenerEnabled;

      const firstName = relatedClient.get('firstName');
      const lastName = relatedClient.get('lastName');
      const birthDate = relatedClient.get('birthDate');

      const hasRequiredFields =
        Boolean(firstName?.trim()) &&
        Boolean(lastName?.trim()) &&
        (Boolean(birthDate) || usePrescreener);

      if (!hasRequiredFields) {
        return;
      }

      const emails = relatedClient.emails?.slice() || [];
      const phones = relatedClient.phones?.slice() || [];
      const firstEmail = emails[0];
      const firstPhone = phones[0];

      if (firstEmail?.get('address')) {
        relatedClient.set('email', firstEmail.get('address'));
      }
      if (firstPhone?.get('number')) {
        relatedClient.set('phoneNumber', firstPhone.get('number'));
      }

      let formattedBirthDate = birthDate;
      if (!usePrescreener) {
        if (birthDate instanceof Date) {
          formattedBirthDate = birthDate.toISOString().split('T')[0];
        } else if (typeof birthDate === 'string' && birthDate.includes('T')) {
          formattedBirthDate = birthDate.split('T')[0];
        } else if (birthDate && typeof birthDate.format === 'function') {
          formattedBirthDate = birthDate.format('YYYY-MM-DD');
        }
      }

      const relatedClientAttrs = {
        billingDelegation: false,
        relatedClientAttributes: {
          firstName,
          lastName,
          nickname: relatedClient.get('nickname') || undefined,
          isMinor: relatedClient.get('isMinor') || false,
          birthDate: formattedBirthDate || undefined,
          billingType: relatedClient.get('billingType') || 0,
          status: 'prospective',
          emailsAttributes: firstEmail ? [{ address: firstEmail.get('address') }] : [],
          phonesAttributes: firstPhone
            ? [
                {
                  number: firstPhone.get('number'),
                  leaveTextMessage: firstPhone.get('leaveTextMessage') ?? true,
                  leaveVoiceMessage: firstPhone.get('leaveVoiceMessage') ?? true,
                },
              ]
            : [],
          state: relatedClient.get('state') || undefined,
          sex: relatedClient.get('sex') || undefined,
          genderInfo: relatedClient.get('genderInfo') || undefined,
        },
      };

      this.client.set('clientRelationshipsAttributes', [relatedClientAttrs]);
    } catch (_) {
      this.client.set('clientRelationshipsAttributes', [
        {
          billingDelegation: false,
        },
      ]);
    }
  }
}
